import { ToolkitStore } from '@reduxjs/toolkit/dist/configureStore'
import { Device, MeterCategory, ThirdPartyVendor } from '../repositories/devices-repository'
import {
    GetPostPaidBillParams,
    PaySmallSmallInstallment,
    InvoiceItems,
    PaySmallSmallBreakdown,
    PaySmallSmallHistoryRequestQuery,
    TopUpMeterPayload,
    Transaction,
    TransactionFilters,
    ServiceRequestPaymentPayload,
    TransferMeterCreditRequest,
    Invoice,
} from '@/types/transactions'
import {
    TransactionStatus,
    PaySmallSmallCheckoutType,
    PaymentProvider,
    TransactionType,
    TransactionTokenType,
} from '@/constants/transactions'
import { Wallet } from '../repositories/wallet-repository'
import { RootState } from '@/store'
import { isFlatRateMeter, isPostpaidOrSLTMeter, isPrepaidMeter } from './utils'
import { PaySmallSmallActions } from '@/store/pay-small-small-reducer'
import {
    PENDING_TRANSACTION_MULTIPLE_CHECK_DURATION,
    PENDING_TRANSACTION_VALIDITY_PERIOD,
    PendingTransactionCheckStage,
    TransactionsActions,
} from './../store/transactions-reducer'
import { CustomerStatementStatus } from '@/constants/reports'
import { stringToLowerCase } from '@/utils'
import TransactionRepository from '@/repositories/transaction-repository'
import DevicesService from '@/services/devices-service'

export default class TransactionService {
    constructor(
        private transactionRepository: TransactionRepository,
        private store: ToolkitStore<RootState>,
        private paySmallSmallActions: PaySmallSmallActions,
        private transactionsActions: TransactionsActions,
        private deviceService: DevicesService,
    ) {}

    isThirdPartyTransaction(transaction: Transaction): boolean {
        return transaction?.vendorId !== undefined
    }

    async getTransactions(filter: TransactionFilters) {
        const [total, transactions] = await Promise.all([
            this.transactionRepository.getTotalTransactionsSpend(filter),
            this.transactionRepository.getTransactions(filter),
        ])

        return {
            total,
            transactions,
        }
    }

    async getThirdPartyTransaction(filter: TransactionFilters) {
        const [total, transactions] = await Promise.all([
            this.transactionRepository.getThirdPartyTotalSpent(filter),
            this.transactionRepository.getThirdPartyTransaction(filter),
        ])

        return {
            total,
            transactions,
        }
    }

    canShowToken(transaction: Transaction) {
        const hasToken = !!transaction.rechargeToken && transaction.rechargeToken !== '0'
        return (
            !this.isThirdPartyTransaction(transaction) &&
            transaction.rechargeMode !== 'REMOTE_ONLY' &&
            hasToken &&
            transaction.meterCategory?.toLowerCase() === MeterCategory.PREPAID &&
            [TransactionStatus.SUCCESS, TransactionStatus.PAID, TransactionStatus.COMPLETED].includes(
                transaction.status.toLowerCase() as TransactionStatus,
            )
        )
    }

    topMeter(
        args: {
            device: Device
            wallet?: Wallet
            isCreateInvoice?: boolean
            isCardPayment?: boolean
            invoiceId?: string
        } & TopUpMeterPayload,
    ) {
        const { fraudBillId, isCreateInvoice, wallet, device, isPayingNonEnergyCharge, invoiceId } = args
        if (!wallet && !isCreateInvoice) {
            throw new Error('Wallet is needed')
        }

        let sourceWalletId = wallet?.id

        if (isCreateInvoice) sourceWalletId = 'ghana.gov'

        const payload = {
            ...args,
            sourceWalletId,
        }

        //  remove these  additinal keys from payload;
        const unUsedKeys = ['device', 'wallet', 'isCreateInvoice'] as Array<keyof typeof payload>

        unUsedKeys.forEach(key => {
            delete payload[key]
        })

        if (isPayingNonEnergyCharge && invoiceId) {
            return this.transactionRepository.payServiceRequestFee(invoiceId, {
                amount: parseFloat(payload.amount.toString()),
                sourceWalletId: sourceWalletId!,
                cardNumber: payload.cardNumber,
                cvv: payload.cvv,
                expiryMonth: payload.expiryMonth,
                expiryYear: payload.expiryYear,
            })
        }

        if (fraudBillId)
            return this.transactionRepository.payFraudBill({
                meterId: device.id,
                billId: fraudBillId,
                payload,
            })

        if (args.isCardPayment) {
            return this.transactionRepository.topUpMeterWithCard(device.id, payload)
        }

        if (args.isPaySmallSmall) {
            return this.transactionRepository.topUpMeterWithPaySmallSmall(device.id, payload)
        }

        return this.transactionRepository.topUpMeter(device.id, payload)
    }

    topUpMeterWithMoMo({ meterId, payload }: { meterId: string; payload: TopUpMeterPayload }) {
        return this.transactionRepository.topUpMeter(meterId, payload)
    }

    topUpMeterWithCard({ meterId, payload }: { meterId: string; payload: TopUpMeterPayload }) {
        return this.transactionRepository.topUpMeterWithCard(meterId, payload)
    }

    topUpThirdPartyMeter(payload: { device: ThirdPartyVendor; amount: string | number; wallet?: Wallet }) {
        if (!payload.wallet) {
            throw new Error('Wallet is needed')
        }

        return this.transactionRepository.topUpThirdPartyMeter(payload.device.id, {
            amount: parseFloat(payload.amount.toString()),
            walletId: payload.wallet?.id,
        })
    }

    payServiceRequestFee(payload: {
        invoiceId: string
        amount: string | number
        sourceWalletId: string
        cardNumber?: string
        cvv?: string
        expiryMonth?: string
        expiryYear?: string
    }) {
        if (!payload.sourceWalletId) {
            throw new Error('Wallet is needed')
        }
        return this.transactionRepository.payServiceRequestFee(payload.invoiceId, {
            amount: parseFloat(payload.amount.toString()),
            sourceWalletId: payload.sourceWalletId,
            cardNumber: payload.cardNumber,
            cvv: payload.cvv,
            expiryMonth: payload.expiryMonth,
            expiryYear: payload.expiryYear,
        })
    }

    async checkTransactionStatus(
        transactionId?: string,
        transactionType?: { isThirdParty?: boolean; isServiceRequest?: boolean },
    ) {
        let res

        if (transactionId) {
            if (transactionType?.isThirdParty) {
                res = await this.transactionRepository.checkThirdPartyTransactionStatus(transactionId)
            } else {
                res = await this.transactionRepository.checkTransactionStatus(
                    transactionId,
                    transactionType?.isServiceRequest,
                )
            }

            if (res.data.status === TransactionStatus.PAID) {
                res.data.status = TransactionStatus.SUCCESS
            }
        }

        return res
    }

    async checkBulkTransactionStatus(batchRechargeId: string, size: number = 5) {
        const res = await this.transactionRepository.checkBulkTransactionStatus(batchRechargeId, size)
        return res
    }

    getTransactionStatusImage(status?: string) {
        status = (status ?? '').toLowerCase()
        const IMAGE_MAP = {
            [TransactionStatus.SUCCESS]: '/images/svg/success.svg',
            [TransactionStatus.PAID]: '/images/svg/success.svg',
            [TransactionStatus.COMPLETED]: '/images/svg/success.svg',
            [TransactionStatus.PENDING]: '/images/svg/pending.svg',
            [TransactionStatus.FAILED]: '/images/svg/failed.svg',
            [TransactionStatus.REFUNDED]: '/images/svg/failed.svg',
            [TransactionStatus.CANCELLED]: '/images/svg/failed.svg',
            [TransactionStatus.UNPAID]: '/images/svg/pending.svg',
            [TransactionStatus.REFUNDED_PENDING]: '/images/svg/pending.svg',
            [TransactionStatus.TRANSACTION_IN_PROGRESS]: '/images/svg/pending.svg',
            [TransactionStatus.ALERT]: '/images/svg/alert-circle.svg',
        } as Record<string, string>

        return IMAGE_MAP[status] ?? '/images/svg/pending.svg'
    }

    getTransactionStatusTexts(status?: string, transaction?: Transaction): { title: string; subtitle: string } {
        status = (status ?? '').toLowerCase()
        const MAP = {
            [TransactionStatus.SUCCESS]: { title: 'Success', subtitle: 'Payment successful' }, //success
            [TransactionStatus.PAID]: { title: 'Success', subtitle: 'Payment successful' }, //success
            [TransactionStatus.COMPLETED]: { title: 'Success', subtitle: 'Payment successful' }, //success
            [TransactionStatus.PENDING]: { title: 'Pending', subtitle: 'Payment pending confirmation' }, //pending
            [TransactionStatus.FAILED]: {
                title: 'Failed',
                subtitle: transaction?.statusDescription ?? 'Payment failed',
            }, //failed
            [TransactionStatus.REFUNDED]: {
                title: 'Failed',
                subtitle: transaction?.statusDescription ?? 'Payment failed',
            }, //failed
            [TransactionStatus.CANCELLED]: {
                title: 'Failed',
                subtitle: transaction?.statusDescription ?? 'Payment failed',
            }, //failed
            [TransactionStatus.UNPAID]: { title: 'Pending', subtitle: 'Payment pending confirmation' }, //pending
            [TransactionStatus.REFUNDED_PENDING]: { title: 'Pending', subtitle: 'Payment pending confirmation' }, //pending
            [TransactionStatus.TRANSACTION_IN_PROGRESS]: { title: 'Pending', subtitle: 'Payment pending confirmation' }, //pending
        } as Record<string, { title: string; subtitle: string }>

        return MAP[status] ?? { title: 'Pending', subtitle: 'Payment pending confirmation' } //pending
    }

    createInvoiceUSSD(id: string, payload: InvoiceItems) {
        return this.transactionRepository.createInvoiceUSSD(id, payload)
    }

    createThirdPartyInvoice(
        meter: ThirdPartyVendor,
        payload: {
            amount: string | number
        },
    ) {
        return this.transactionRepository.createThirdPartyInvoice(meter.id, {
            name: meter.companyName,
            description: `Invoice for ${meter.companyName} for ${meter.vendorName}`,
            quantity: 1,
            unitPrice: parseFloat(payload.amount.toString()),
            lineTotal: 1,
        })
    }
    createGeneralMeterInvoice(
        meterId: string,
        payload: {
            amount: string | number
            sourceWalletId: string
        },
    ) {
        return this.transactionRepository.createGeneralMeterInvoice(meterId, payload)
    }

    private getCustomerStatementStatus(transaction: Transaction): TransactionStatus | CustomerStatementStatus {
        const customerStatus = stringToLowerCase(transaction.customerStatementStatus) as CustomerStatementStatus

        if (!customerStatus) {
            return TransactionStatus.PENDING
        }

        if (this.isFailedOrRefundedStatus(transaction.status)) {
            return TransactionStatus.FAILED
        }

        if (customerStatus === CustomerStatementStatus.Completed) {
            return TransactionStatus.SUCCESS
        }

        if (this.isPendingCustomerStatus(customerStatus)) {
            return TransactionStatus.PENDING
        }

        if (this.isFailedCustomerStatus(customerStatus)) {
            return TransactionStatus.FAILED
        }

        return customerStatus
    }

    private isFailedOrRefundedStatus(status?: string): boolean {
        const lowerStatus = stringToLowerCase(status)
        return [TransactionStatus.REFUNDED, TransactionStatus.FAILED].includes(lowerStatus as TransactionStatus)
    }

    private isPendingCustomerStatus(status: CustomerStatementStatus): boolean {
        return [
            CustomerStatementStatus.Processing,
            CustomerStatementStatus.RefundFailed,
            CustomerStatementStatus.DelayedRefund,
        ].includes(status)
    }

    private isFailedCustomerStatus(status: CustomerStatementStatus): boolean {
        return [
            CustomerStatementStatus.Failed,
            CustomerStatementStatus.Refunded,
            CustomerStatementStatus.RefundPending,
        ].includes(status)
    }

    private getPrepaidStatus(transaction: Transaction): TransactionStatus {
        if (
            (!transaction.isRechargeFulfilled &&
                (stringToLowerCase(transaction.status) === TransactionStatus.SUCCESS ||
                    stringToLowerCase(transaction.status) === TransactionStatus.COMPLETED)) ||
            stringToLowerCase(transaction.status) === TransactionStatus.PAID
        ) {
            return TransactionStatus.PENDING
        }

        if (
            transaction.isRechargeFulfilled &&
            (stringToLowerCase(transaction.status) === TransactionStatus.SUCCESS ||
                stringToLowerCase(transaction.status) === TransactionStatus.COMPLETED)
        ) {
            return TransactionStatus.SUCCESS
        }

        return stringToLowerCase(transaction.status) as TransactionStatus
    }

    private getNonPrepaidStatus(transaction: Transaction): TransactionStatus {
        const lowerStatus = stringToLowerCase(transaction.status)
        if (
            [TransactionStatus.PAID, TransactionStatus.SUCCESS, TransactionStatus.COMPLETED].includes(
                lowerStatus as TransactionStatus,
            )
        ) {
            return TransactionStatus.SUCCESS
        }

        return lowerStatus as TransactionStatus
    }

    getFinalTransactionStatus(transaction: Transaction): TransactionStatus | CustomerStatementStatus {
        if (!transaction.status) {
            return TransactionStatus.PENDING
        }

        if (this.isCustomerStatement(transaction)) {
            return this.getCustomerStatementStatus(transaction)
        }

        if (this.isPrepaidTransaction(transaction)) {
            return this.getPrepaidStatus(transaction)
        }

        return this.getNonPrepaidStatus(transaction)
    }

    isPrepaidTransaction(transaction: Transaction): boolean {
        return [MeterCategory.PREPAID, MeterCategory.PREPAID_OFFLINE].includes(
            transaction.meterCategory?.toLowerCase() as MeterCategory,
        )
    }

    isCustomerStatement(transaction: Transaction) {
        return transaction?.transactionType === TransactionType.customerStatement
    }

    isTransactionSuccessful(transactionStatus?: TransactionStatus | string | null) {
        return [TransactionStatus.SUCCESS, TransactionStatus.COMPLETED].includes(transactionStatus as TransactionStatus)
    }

    isTransactionPending(transaction: Transaction) {
        return [TransactionStatus.PENDING, TransactionStatus.REFUNDED_PENDING, TransactionStatus.UNPAID].includes(
            transaction.status,
        )
    }

    formatMoney(amount?: number | string | null, decimalPlaces: number = 2): string {
        let value = this.roundToDecimalPlace(amount ?? 0, decimalPlaces).toLocaleString(undefined, {
            minimumFractionDigits: decimalPlaces,
        })
        return value
    }

    roundToEven(num: number, precision: number = 2): number {
        const multiplier = Math.pow(10, precision)
        return Math.floor((num ?? 0) * multiplier + 0.5) / multiplier
    }

    roundToDecimalPlace(num: number | string, decimalPlace: number = 2): number {
        if (+num < 0) return -this.roundToDecimalPlace(-num, decimalPlace)
        const p = Math.pow(10, decimalPlace)
        const n = +(+num * p).toPrecision(15)
        return Math.round(n) / p
    }

    getSingleInvoice(id: string) {
        return this.transactionRepository.getSingleInvoice(id)
    }

    getPostpaidBillUrl(payload: GetPostPaidBillParams) {
        return this.transactionRepository.getPostpaidBillUrl(payload)
    }

    getConsumptionData(accountNumber: string, period = 'all') {
        return this.transactionRepository.getConsumptionData({
            accountNumber,
            period,
        })
    }

    async getDailyConsumption(args: { meter: Device }) {
        const { meter } = args
        const payload = {
            accountNumber: meter.accountNumber,
        }

        return {
            postpaid: isPostpaidOrSLTMeter(meter)
                ? await this.transactionRepository.getPostpaidDailyConsumption(payload)
                : null,
            flatRate: isFlatRateMeter(meter)
                ? await this.transactionRepository.getFlatRateDailyConsumption(payload)
                : null,
            prepaid: isPrepaidMeter(meter) ? await this.transactionRepository.getDailyConsumption(payload) : null,
        }
    }

    getConsumptionForMonths(payload: { accountNumber: string; numberOfMonths: number }) {
        return this.transactionRepository.getConsumptionForMonths(payload)
    }

    getConsumptionFor(payload: { accountNumber: string }) {
        return this.transactionRepository.getConsumptionFor(payload)
    }

    getConsumptionRunOutDate(payload: { accountNumber: string }) {
        return this.transactionRepository.getConsumptionRunOutDateFor(payload)
    }

    getPostpaidConsumptionData(payload: { accountNumber: string; period: string }) {
        return this.transactionRepository.getPostpaidConsumptionData(payload)
    }

    getPostpaidPaymentHistory(param: TransactionFilters) {
        return this.transactionRepository.getPostpaidPaymentHistory(param)
    }

    getFlatRateConsumptionData(payload: { accountNumber: string }) {
        return this.transactionRepository.getFlatRateConsumptionData(payload)
    }

    getUserFriendlyTransactionChannel(channel: string) {
        const PAYMENT_PROVIDER_LABLE_MAP: Record<string, string> = {
            mobilemoney: 'Mobile Money',
            'ghana.gov': 'Pay At Any Bank',
        }

        return PAYMENT_PROVIDER_LABLE_MAP[channel] || channel
    }

    async getFraudBills(accountNumber: string) {
        const res = await this.transactionRepository.getFraudBills(accountNumber)

        let installments = res.data.installments.map((installment, index) => {
            const amountLeft = installment.amount - installment.amountPaid

            return {
                ...installment,
                amountLeft,
                canMakePayment: false,
                isPaid: amountLeft <= 0,
            }
        })

        let currentInstallment: Record<string, any> | null = null

        installments.forEach((installment, index) => {
            if (currentInstallment) return

            currentInstallment = installment.amountLeft > 0 ? installments : null
            if (currentInstallment) installment.canMakePayment = true
        })

        res.data.installments = installments
        return res
    }

    async enroll3dsCard(meterId: string, referenceId: string) {
        return this.transactionRepository.enroll3dsCard(meterId, referenceId)
    }

    async completeEnroll3dsCard(meterId: string, referenceId: string) {
        return this.transactionRepository.completeEnroll3dsCard(meterId, referenceId)
    }

    async completeTopUpCardPayment(meterId: string, transactionId: string) {
        return this.transactionRepository.completeCardPayment(meterId, transactionId)
    }

    async nonEnergyEnroll3dsCard(invoiceId: string, referenceId: string) {
        return this.transactionRepository.nonEnergyEnroll3dsCard(invoiceId, referenceId)
    }

    async nonEnergyCompleteCardPayment(invoiceId: string, referenceId: string) {
        return this.transactionRepository.nonEnergyCompleteCardPayment(invoiceId, referenceId)
    }

    async getPaySmallSmallTransactionBreakdown(transactionId?: string) {
        return await this.transactionRepository.getPaySmallSmallTransactionBreakdown(transactionId)
    }

    async getPaySmallSmallHistoryList(query?: Partial<PaySmallSmallHistoryRequestQuery>) {
        const res = await this.transactionRepository.getPaySmallSmallHistoryList(query ?? {})
        return res
    }

    getMostRecentPaidItem(transactions: PaySmallSmallInstallment[] = []): PaySmallSmallInstallment | null {
        let mostRecentPaidItem: PaySmallSmallInstallment | null = null
        let mostRecentPaidDate = new Date(0)

        for (const transaction of transactions) {
            if (transaction.status?.toLowerCase() === 'paid') {
                const transactionDate = new Date(transaction.repaymentDate)

                if (transactionDate >= mostRecentPaidDate) {
                    mostRecentPaidDate = transactionDate
                    mostRecentPaidItem = transaction
                }
            }
        }

        return mostRecentPaidItem
    }

    getNextRepayment(transactions: PaySmallSmallInstallment[] = []): PaySmallSmallInstallment | null {
        const nextItemToBePaid = transactions.filter(item => item.status.toLowerCase() === TransactionStatus.UNPAID)[0]

        return nextItemToBePaid
    }

    hasLastUnpaidRepayment(transactions: PaySmallSmallInstallment[] = []): boolean {
        const nextItemToBePaid =
            transactions.filter((transaction, index) => {
                return transaction.status.toLowerCase() == TransactionStatus.UNPAID
            }).length == 1

        return nextItemToBePaid
    }

    getTotalOutstandingAmount(transactions: PaySmallSmallInstallment[] = []): number {
        return (
            transactions
                .filter(item => item.status.toLowerCase() !== 'paid')
                .reduce((acc, curr) => curr.amount + acc, 0) ?? 0
        )
    }

    hasCompletedPayment(transactions: PaySmallSmallInstallment[] = []): boolean {
        return transactions?.every(transaction => transaction.status.toLowerCase() == 'paid')
    }

    getPaySmallSmallPaymentStatus(breakdown: PaySmallSmallBreakdown): string | TransactionStatus {
        if (this.hasCompletedPayment(breakdown.installments)) {
            return TransactionStatus.PAID
        } else if (
            breakdown.installments.some(item => item.isMissedPayment) &&
            breakdown.repaymentStatus.toLowerCase() === TransactionStatus.UNPAID
        ) {
            return 'overdue'
        } else {
            return breakdown.repaymentStatus.toLowerCase()
        }
    }

    async makePaySmallSmallManualRepayment(payload: {
        amount: string | number
        transactionId: string
        callbackUrl: string
        debitMobileNumber: string
        channel: string
    }) {
        return this.transactionRepository.makePaySmallSmallManualRepayment(payload)
    }

    async checkPaySmallSmallManualRepaymentStatus(transactionId: string) {
        return this.transactionRepository.checkPaySmallSmallManualRepaymentStatus(transactionId)
    }

    getPaySmallSmallStatusTagImage = (status: string) => {
        switch (status?.toLowerCase()) {
            case 'paid':
                return '/images/icons/transaction/completed.svg'

            case 'completed':
                return '/images/icons/transaction/completed.svg'

            case 'ongoing':
                return '/images/icons/transaction/ongoing.svg'

            case 'overdue':
                return '/images/icons/transaction/overdue.svg'
            case 'cancelled':
                return '/images/icons/transaction/cancelled.svg'

            default:
                return '/images/icons/transaction/ongoing.svg'
        }
    }

    async paySmallSmallUserHasOverdueDebt() {
        const res = await this.getPaySmallSmallBorrowerData()
        this.store.dispatch(
            this.paySmallSmallActions.setState({
                userHasOverdueDebt: (res?.repayments?.totalOverdueAmount ?? 0) > 0,
            }),
        )
        return (res?.repayments?.totalOverdueAmount ?? 0) > 0
    }

    async getPaySmallSmallPaymentChannelStatus() {
        const res = await this.transactionRepository.getPaySmallSmallPaymentChannelStatus()
        return res.data
    }

    async getPaySmallSmallStatus() {
        const res = await this.transactionRepository.getPaySmallSmallStatus()
        return res?.data
    }

    async getPaySmallSmallCreditScore() {
        const res = await this.transactionRepository.getPaySmallSmallCreditScore()
        this.store.dispatch(
            this.paySmallSmallActions.setState({
                creditScore: res?.data,
            }),
        )
        return res?.data
    }

    async getPaySmallSmallBorrowerData() {
        const res = await this.transactionRepository.getPaySmallSmallBorrowerData()
        this.store.dispatch(
            this.paySmallSmallActions.setState({
                borrower: res?.data,
            }),
        )
        return res?.data
    }

    async getPaySmallSmallCreditConfig() {
        const res = await this.transactionRepository.getPaySmallSmallCreditConfig()
        this.store.dispatch(
            this.paySmallSmallActions.setState({
                config: res?.data,
            }),
        )
        return res?.data
    }

    async getPaySmallSmallCheckoutConfig(payload: {
        checkoutType: PaySmallSmallCheckoutType | string
        installmentId?: string
        transactionId?: string
    }) {
        const res = await this.transactionRepository.getPaySmallSmallCheckoutConfig(payload)
        return res?.data
    }

    async getPaySmallSmallCreditLimitPercentage() {
        const res = await this.transactionRepository.getPaySmallSmallCreditLimitPercentage()
        this.store.dispatch(
            this.paySmallSmallActions.setState({
                creditLimitPercentage: res?.data,
            }),
        )
        return res?.data
    }

    async getPaySmallSmallOutstandingCreditInfo() {
        const res = await this.transactionRepository.getPaySmallSmallOutstandingCreditInfo()
        this.store.dispatch(
            this.paySmallSmallActions.setState({
                outstandingCreditInfo: res.data,
            }),
        )
        return res?.data
    }

    async getOverduePayments() {
        const res = await this.transactionRepository.getOverduePayments()
        return res.data
    }

    async userHasOverduePayments() {
        const res = await this.transactionRepository.getOverduePayments()
        if (res.data.length > 0) {
            this.store.dispatch(
                this.paySmallSmallActions.setState({
                    userHasOverdueDebt: true,
                }),
            )
            return true
        }
        return false
    }

    async initiateAllOutstandingPayments() {
        const res = await this.transactionRepository.initiateAllOutstandingPayments()
        return res.data.data
    }
    async initiateDueNowPayment(payload: { transactionId: string; installmentId: string }) {
        const res = await this.transactionRepository.initiateDueNowPayment(payload)
        return res.data.data
    }
    async initiateAllDuePayments(transactionId: string) {
        const res = await this.transactionRepository.initiateAllDuePayments(transactionId)
        return res.data.data
    }

    async getPaySmallSmallTransaction(id: string) {
        const res = await this.transactionRepository.getPaySmallSmallTransaction(id)
        return res.data
    }

    async activatePaySmallSmallUser(payload: { profilePicture: string; ghanaCardId: string }) {
        const res = await this.transactionRepository.activatePaySmallSmallUser(payload)
        return res.data
    }

    makeServiceRequestPayment(args: ServiceRequestPaymentPayload) {
        if (!args.sourceWalletId) {
            throw new Error('Wallet is needed')
        }

        return this.transactionRepository.makeServiceRequestPayment(args)
    }

    async completeChangeAccountDetailsCardPayment(transactionId: string) {
        return this.transactionRepository.completeChangeAccountDetailsCardPayment(transactionId)
    }

    getPaymentProvider(channel: string) {
        switch (channel?.toLowerCase()) {
            case PaymentProvider.FINERACT:
                return 'My PowerApp Balance'
            case PaymentProvider.POWERAPP_BALANCE:
                return 'My PowerApp Balance'
            case PaymentProvider.CARD:
                return 'Bank Card'
            case PaymentProvider.GHANA_GOV:
                return 'Cash At Any Bank'
            case PaymentProvider.GMONEY:
                return 'G-Money'
            case PaymentProvider.OLTPV:
                return 'OLTPV'
            default:
                return 'Mobile Money'
        }
    }

    async meterHasPreviousTransactions(meterId: string) {
        const res = await this.transactionRepository.meterHasPreviousTransactions(meterId)
        return res.data
    }

    isValidPendingTransaction = (transaction?: Transaction | null, meter?: Device) => {
        const isTokenOnly = transaction?.rechargeMode?.toLowerCase() !== 'remote_only' //TD: consult with backend to figure out why recharge mode is null until token is issued

        const isNonEnergyMeter = meter?.meterCategory === MeterCategory.NON_ENERGY

        const isFlatRateMeter = this.deviceService.isFlatRateMeter(meter)

        const isNonSmartMeter = this.deviceService.isAKnownOfflineMeter(meter)

        const isLessThan15Mins =
            transaction &&
            Date.now() - new Date(transaction.createdAt).getTime() < PENDING_TRANSACTION_VALIDITY_PERIOD * 60 * 1000

        const isNotCustomerStatement = transaction?.transactionType !== TransactionType.customerStatement

        const isSuccessfulTransaction = this.isTransactionSuccessful(transaction?.status)

        const isPending =
            (isTokenOnly &&
                isSuccessfulTransaction &&
                transaction?.isPaymentCompleted &&
                !transaction.isRechargeFulfilled &&
                !transaction?.rechargeToken) ||
            transaction?.status === TransactionStatus.PENDING

        const isValid =
            !isNonEnergyMeter && !isNonSmartMeter && !isFlatRateMeter && isLessThan15Mins && isNotCustomerStatement

        return isValid && isPending
    }

    checkStatusForPendingTransaction = async ({
        transactionId,
        onNavigate,
    }: {
        transactionId: string
        onNavigate?: () => void
    }) => {
        const res = await this.transactionRepository.checkTransactionStatus(transactionId)
        const transaction = res.data

        const isPendingTransactionValid = await this.isValidPendingTransaction(transaction)

        const pendingTransaction = this.store.getState().transactions.pendingTransactions[transactionId]

        if (!isPendingTransactionValid) {
            this.store.dispatch(this.transactionsActions.filterOldTransactions())
        } else {
            const now = new Date()
            if (
                now.getTime() - new Date(transaction.createdAt).getTime() <
                    PENDING_TRANSACTION_VALIDITY_PERIOD * 60 * 1000 &&
                transaction.status === 'pending' &&
                transaction.isPaymentCompleted
            ) {
                if (pendingTransaction?.stage === PendingTransactionCheckStage.wait) {
                    this.store.dispatch(
                        this.transactionsActions.updatePendingTransaction({
                            transactionId,
                            stage: PendingTransactionCheckStage.first,
                            updatedAt: now,
                            createdAt: now,
                            countdown: PENDING_TRANSACTION_MULTIPLE_CHECK_DURATION * 60,
                        }),
                    )
                } else if (
                    pendingTransaction?.stage === PendingTransactionCheckStage.first ||
                    pendingTransaction?.stage === PendingTransactionCheckStage.second
                ) {
                    this.store.dispatch(
                        this.transactionsActions.updatePendingTransaction({
                            transactionId,
                            stage:
                                pendingTransaction?.stage === PendingTransactionCheckStage.first
                                    ? PendingTransactionCheckStage.second
                                    : PendingTransactionCheckStage.third,
                            updatedAt: now,
                            countdown: PENDING_TRANSACTION_MULTIPLE_CHECK_DURATION * 60,
                        }),
                    )
                } else {
                    this.store.dispatch(
                        this.transactionsActions.updatePendingTransaction({
                            transactionId,
                            meterName: transaction.meterName,
                            stage: PendingTransactionCheckStage.wait,
                            status: transaction.status,
                            countdown: PENDING_TRANSACTION_MULTIPLE_CHECK_DURATION * 60,
                            updatedAt: now,
                            createdAt: now,
                        }),
                    )
                }
            }
            this.store.dispatch(this.transactionsActions.filterOldTransactions())
            onNavigate?.()
        }
        return transaction
    }

    getTransactionTokens(token?: string | null): string[] {
        if (!token) {
            return []
        }
        const tokenList = token.split(';')
        return tokenList
    }

    getTokenType(token?: string | null): TransactionTokenType {
        if (!token) {
            return TransactionTokenType.NONE
        }
        const tokenList = this.getTransactionTokens(token)
        return tokenList.length > 1 ? TransactionTokenType.MULTIPLE : TransactionTokenType.SINGLE
    }

    async transferMeterCredit(payload: TransferMeterCreditRequest) {
        return await this.transactionRepository.transferMeterCredit(payload)
    }

    async getMeterCreditTransferDetails(transferId: string) {
        return await this.transactionRepository.getMeterCreditTransferDetails(transferId)
    }

    convertInvoiceToTransaction = (invoice: Invoice): Transaction => {
        if (!invoice) {
            throw new Error('Invoice is required')
        }
        // Convert invoice items to orderItems format
        const orderItems = invoice.invoiceItems.map(item => ({
            name: item.name,
            description: item.description,
            quantity: item.quantity.toString(),
            unitPrice: item.unitPrice.toString(),
            lineTotal: item.lineTotal.toString(),
        }))

        // Create transaction with only fields that have corresponding values in the invoice
        const transaction: Partial<Transaction> = {
            id: invoice.id,
            amount: parseFloat(invoice.subTotal.toString()),
            status: invoice.status as TransactionStatus,
            createdAt: invoice.createdAt,
            updatedAt: invoice.updatedAt,
            customerName: invoice.recipientName,
            phoneNumber: invoice.recipient, // Assuming recipient is phone number
            meterId: invoice.meterId,
            meterName: invoice.meterName ?? '',
            correlationId: invoice.invoiceNumber,
            invoiceId: invoice.invoiceNumber,
            statusDescription: invoice.status,
            isPaymentCompleted: invoice.amountPaid === invoice.subTotal,
            // Order information
            orderItems,
            createdBy: invoice.createdBy,
            outStandingDebtAmount: parseFloat(invoice.debt.toString()),
        }

        return transaction as Transaction
    }

    async getPssExistingCreditAllocationInfo() {
        try {
            const res = await this.transactionRepository.getPssExistingCreditAllocationInfo()
            this.store.dispatch(
                this.paySmallSmallActions.setState({
                    creditAllocationData: res.data,
                }),
            )
            return res?.data
        } catch {
            this.store.dispatch(
                this.paySmallSmallActions.setState({
                    creditAllocationData: null,
                }),
            )
            return null
        }
    }

    async getOutstandingCreditAllocationInfo() {
        try {
            const res = await this.transactionRepository.getOutstandingCreditAllocationInfo()
            this.store.dispatch(
                this.paySmallSmallActions.setState({
                    creditAllocationData: res.data,
                }),
            )
            return res?.data
        } catch {
            this.store.dispatch(
                this.paySmallSmallActions.setState({
                    creditAllocationData: null,
                }),
            )
            return null
        }
    }

    async getBorrowerVerificationStatus() {
        const res = await this.transactionRepository.getBorrowerVerificationStatus()
        return res?.data
    }
}
